import { useCallback, useContext, useMemo, useState } from "react";
import { isEqual, omit } from "lodash";
import { PageStaticDataContext } from "../contexts/PageStaticDataContextProvider";
import { ProductContext } from "../contexts/ProductContextProvider";
import { getPreselectedOptionsFromUrl } from "../utils/defaultSelections";
import { MpvProvider } from "../utils/MpvProvider";
import { getStudioDocumentUrl } from "../utils/ReferentialImageryHelper";
import TrackingUtil from "../utils/tracking";
import { Translations } from "../utils/Translation";
import { useSnackbarContext } from "../contexts/SnackbarContext";

const useGetStudioDocumentUrl = (): {
  studioDocumentUrl?: string;
} => {
  const { mpv } = useContext(PageStaticDataContext);
  const { selections } = useContext(ProductContext);
  const { showSnackbarMessage } = useSnackbarContext();
  const [studioDocumentUrl, setStudioDocumentUrl] = useState<string | undefined>(undefined);

  const [defaultSelectionsFromUrl, isUrlContainingAllOptions] = useMemo(() => {
    const mpvProvider = new MpvProvider(mpv);
    const productPageMerchDetails = mpvProvider.getProductPageMerchandisingDetails();
    const productPageMerchDetailsProperties = productPageMerchDetails?.properties || [];
    const defaultSelectionsFromUrl = getPreselectedOptionsFromUrl({
      mpv,
      merchandisingDetailProperties: productPageMerchDetailsProperties,
    });

    const isUrlContainingAllOptions =
      Object.keys(defaultSelectionsFromUrl).length === productPageMerchDetailsProperties.length;

    return [defaultSelectionsFromUrl, isUrlContainingAllOptions];
  }, [mpv]);

  const trackStudioDocumentUrlInvalidatedEvent = useCallback(() => {
    TrackingUtil.trackExperimentViewed(mpv.mpvId, {
      label: "Document URL invalidated",
      pageStage: "Discover",
      experimentId: "23263560084",
      experimentName: "Preview with your Design LAT Discovery Page",
    });
  }, [mpv.mpvId]);

  useMemo(() => {
    if (
      selections &&
      isUrlContainingAllOptions &&
      isEqualExcludingProperties(selections, defaultSelectionsFromUrl, "Substrate Color")
    ) {
      setStudioDocumentUrl(getStudioDocumentUrl());
    } else {
      if (studioDocumentUrl) {
        trackStudioDocumentUrlInvalidatedEvent();
        showSnackbarMessage(
          Translations.getInstance().getTranslation("shopWithYourDesignWarningMessage"),
          "warning"
        );
      }
      setStudioDocumentUrl(undefined);
    }
  }, [defaultSelectionsFromUrl, selections]);

  return {
    studioDocumentUrl,
  };

  function isEqualExcludingProperties(
    object1: object,
    object2: object,
    ...propertiesToExclude: string[]
  ): boolean {
    return isEqual(omit(object1, propertiesToExclude), omit(object2, propertiesToExclude));
  }
};

export default useGetStudioDocumentUrl;
